import { LoadingButton } from '@mui/lab';
import {
  AlertTitle,
  Box,
  FormControl,
  IconButton,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChangeEvent,
  FC,
  MouseEventHandler,
  useCallback,
  useEffect,
} from 'react';
import { MdContentCopy as ContentCopyIcon } from 'react-icons/md';
import { makeStyles } from 'tss-react/mui';
import { useSnapshot } from 'valtio';
import { Nullable } from 'common-types';
import { Organization } from '../../organizations/organization-types';
import { updateOrgDisplayName } from '../actions/org-settings-actions';
import RemoveOrgContainer from '../containers/RemoveOrgContainer';
import {
  setIsOrgDisplayNameUpdated,
  setOrgDisplayName,
} from '../store-actions/org-settings-store-actions';
import OrgSettingsStore from '../stores/org-settings-store';
import SeverityAlert from './SeverityAlert';

const OrgSettingsTab: FC<React.PropsWithChildren<OrgSettingsTabProps>> = ({
  chosenOrganization,
  isUserAdminRole,
  showRemoveOrgModal,
}) => {
  const { classes } = useStyles();

  const {
    isLoadingUpdateOrgDisplayName,
    isOrgDisplayNameUpdated,
    orgDisplayName,
    isValidOrgDisplayName,
    invalidOrgDisplayNameHelperText,
  } = useSnapshot(OrgSettingsStore, { sync: true });

  const copyToClipboard = useCallback(() => {
    if (chosenOrganization && chosenOrganization.id) {
      navigator.clipboard.writeText(chosenOrganization.id);
    }
  }, [chosenOrganization]);

  const handleOrgDisplayChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (chosenOrganization) {
        const { value } = event.target;
        setOrgDisplayName(value);
        setIsOrgDisplayNameUpdated(value, chosenOrganization.display_name);
      }
    },
    [chosenOrganization],
  );

  useEffect(() => {
    if (!orgDisplayName && chosenOrganization) {
      setOrgDisplayName(chosenOrganization.display_name);
    }
  }, [orgDisplayName, chosenOrganization]);

  if (chosenOrganization === null) {
    return null;
  }

  return (
    <div className={classes.orgSettingsTab}>
      <RemoveOrgContainer />
      <div className={classes.fields}>
        <Tooltip arrow title='Organization display name' placement='right'>
          <FormControl>
            <TextField
              variant='standard'
              size='small'
              value={orgDisplayName}
              label='Organization Name:'
              onChange={handleOrgDisplayChange}
              helperText={
                <Typography component='span' color='error'>
                  {invalidOrgDisplayNameHelperText}
                </Typography>
              }
              InputProps={{
                endAdornment: (
                  <Box mb={1}>
                    <LoadingButton
                      loading={isLoadingUpdateOrgDisplayName}
                      size='small'
                      variant='contained'
                      color='primary'
                      disabled={
                        !isOrgDisplayNameUpdated || !isValidOrgDisplayName
                      }
                      onClick={updateOrgDisplayName}>
                      Save
                    </LoadingButton>
                  </Box>
                ),
              }}
            />
          </FormControl>
        </Tooltip>
        <Tooltip arrow title='Organization identifier' placement='right'>
          <FormControl>
            <TextField
              variant='standard'
              size='small'
              value={chosenOrganization.id}
              label='Organization ID:'
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <Tooltip arrow title='Copy organization ID'>
                    <span>
                      <IconButton onClick={copyToClipboard} size='small'>
                        <ContentCopyIcon color='success' fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                ),
              }}
            />
          </FormControl>
        </Tooltip>
      </div>
      <SeverityAlert
        buttonToolTipText={
          !isUserAdminRole
            ? 'Read only user cannot delete the organization'
            : `Delete ${chosenOrganization.display_name} organization`
        }
        severity='error'
        buttonText='delete'
        enableButton={isUserAdminRole}
        onAction={showRemoveOrgModal}>
        <AlertTitle>Delete organization</AlertTitle>
        <Typography flexGrow={1} variant='body2'>
          Deleting an organization is an irreversible action. Please make sure
          this is the outcome you want before taking this action.
        </Typography>
      </SeverityAlert>
    </div>
  );
};

interface OrgSettingsTabProps {
  chosenOrganization: Nullable<Organization>;
  isUserAdminRole: boolean;
  showRemoveOrgModal: MouseEventHandler<HTMLButtonElement>;
}

const useStyles = makeStyles()((theme: Theme) => ({
  orgSettingsTab: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '30%',
    [theme.breakpoints.down('lg')]: {
      width: '50%',
    },
  },
}));

export default OrgSettingsTab;
